/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {
  selectCurrentParameterListState,
  selectInputData,
  selectOutputData,
  setInputData,
  setOutputData,
  setOutputsNotUpdated
} from '../../../../redux/ducks/ScenarioAnalysis/slice';
import {useAppDispatch} from '../../../../redux/hooks';
import {RootState} from '../../../../redux/store';

import {usePostRequest} from "../../../../api/helper";
import ShapePlot from './ShapePlot';
import toast from "react-hot-toast";
import {MRT_Cell, MRT_ColumnDef} from 'material-react-table';
import ArgosSelect from '../../../components/Form/Select/ArgosSelect';
import {useHasWriteAccess} from '../../../../redux/ducks/AccessControl/accessControlHelpers';
import ArgosMenuItem from '../../../components/Form/Select/ArgosMenuItem';
import {SCENARIO_ANALYSIS_CONSTANTS} from '../constants';
import ArgosFlexbox from '../../../components/Containers/ArgosFlexbox';
import {Typography} from '@mui/material';
import {ARGOS_ERROR} from '../../../../constants/colors';
import ArgosButton, {ArgosLoadingButton} from '../../../components/Buttons/ArgosButton';
import {ArgosExecuteIcon} from '../../../components/Icons/ArgosDatasetControlPanelIcons';
import {ArgosReloadIcon} from '../../../components/Icons/ArgosActionIcons';
import {isShapeModel} from '../../../../helpers/models';
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";


interface VariablesTableProps {
  tableData: any
  model: string
  version: string
  variation: string
}


interface EnumeratedVariableValues {
  value: number
  alias: string
}


export interface VariablesTableRow {
  display_name: string
  variable_name: string
  i_o: string
  data_type: string
  description: string
  enum_data: EnumeratedVariableValues[]
  min: number
  max: number
  default: number
  units: string
  value?: any /* Editable value initalized as default */
}


const transformData = (editableData: VariablesTableRow[]) => {
  const res: any = {}
  editableData.forEach((element: VariablesTableRow) => {
    const value = Number(element.value)  /* Enforce that only number type is passed to model execution */
    const fullVariableName = element.variable_name
    const executionVariable = fullVariableName.includes("shape.") 
      ? fullVariableName.replace("shape.", "")  /* Shape model execution doesn't use the prefix */
      : fullVariableName
    res[executionVariable] = value
  })
  return res
}


const filterAndMap = (table_data: VariablesTableRow[], i_o: string): VariablesTableRow[] => {
  const inputs_lst = table_data.filter((v: VariablesTableRow) =>
    v.i_o === i_o
  )
  const lst = inputs_lst.map((v: VariablesTableRow) => {
    const copyRow = {...v}
    copyRow.value = v.default
    return copyRow
  })
  return lst
}


const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%'
})


/**
 * Renders the variable inputs and outputs table on the View Model page
 */
const VariablesTable = (props: VariablesTableProps) => {
  const {tableData, model, variation, version} = props
  
  const [isModelExecutionLoading, setIsModelExecutionLoading] = useState(false)
  const [firstExecute, setFirstExecute] = useState(false)
  
  const hasWriteAccess = useHasWriteAccess()
  const dispatch = useAppDispatch()
  const inputData: VariablesTableRow[] = useSelector(selectInputData)
  const outputData: VariablesTableRow[] = useSelector(selectOutputData)
  const outputsNotUpdated: boolean = useSelector((state: RootState) => selectCurrentParameterListState(state)?.outputsNotUpdated)
  const displayInvalidLabel = outputsNotUpdated && firstExecute ? 'initial' : 'none'
  
  const [shapesData, setShapesData] = useState([])
  
  const [disableHostAccelerationProfile, setDisableHostAccelerationProfile] = useState<boolean>(true)
  const overrideHostAcceleration = 0
  const [validationErrors, setValidationErrors] = useState<any>({})
  
  useEffect(() => {
    /**
     * TODO: should be setting this when we first fetch it now that using redux, dont pass as prop
     */
    const lst: VariablesTableRow[] = filterAndMap(tableData, "input")
    dispatch(setInputData(lst))
    const _outputData = tableData.filter((v: VariablesTableRow) =>
      v.i_o === "output")
    dispatch(setOutputData(_outputData))
  }, [tableData])

  const handleSaveCell = (newVal: any, rowIndex: number) => {
    const value: any = newVal
    const singleDash = value === '-'
    const regex = /^[-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/
    if (value === "" && !singleDash && !regex.test(value)) {
      return
    }

    /**
     * update rows
     */
    const copyData = [...inputData]
    const copyRow = {...copyData[rowIndex]}
    copyRow.value = value
    copyData.splice(rowIndex, 1, copyRow)
    dispatch(setInputData(copyData))

    /**
     * set indicator that model has not been run with latest inputs
     */
    dispatch(setOutputsNotUpdated(true))
  }

  const checkIfSelected = (e: any) => {
    let itWasSelected = true
    if (overrideHostAcceleration === e.target.value) {
      itWasSelected = false
    }
    return itWasSelected
  }

  const getCommonEditTextFieldProps = (cell: MRT_Cell<VariablesTableRow>): MRT_ColumnDef<VariablesTableRow>['muiEditTextFieldProps'] => {
    return {
      error: !!validationErrors[cell.id],
      helperText: validationErrors[cell.id],
      onKeyDown: (event: any) => {
        if (event.code == "Tab") {
          event.stopPropagation()
          event.preventDefault()
        } else if (event.code == 'ArrowRight') {
          const isValid = isValueInputValid(event.target.value, cell)
          if (isValid) {
            handleSaveCell(event.target.value, cell.row.index)
          }
        }
      },
      onBlur: (event) => {
        const isValid = isValueInputValid(event.target.value, cell)
        if (isValid) {
          handleSaveCell(event.target.value, cell.row.index)
        }
      },
    }
  }


  /**
   * Validate input value
   */
  const isValueInputValid = (value: string, cell: MRT_Cell<VariablesTableRow>) => {
    /* Check if input is non-empty */
    if (value === undefined || value === null || value == "") {
      const errorMessage = "Value must not be empty"
      setValidationErrors({
        ...validationErrors,
        [cell.id]: errorMessage
      })
      toast.error(errorMessage)
      return false
    }

    /* Chck if is a number */
    const numberValue = Number(value)
    if (isNaN(numberValue)) {
      const errorMessage = "Value must be a number"
      setValidationErrors({
        ...validationErrors,
        [cell.id]: errorMessage
      })
      toast.error(errorMessage)
      return false
    }

    /* Check if number is in range of min and max */
    const min = cell.row.original.min
    const max = cell.row.original.max
    if ((min != null && numberValue < min) || (max != null && numberValue > max)) {
      const errorMessage = `Value must be between ${min} and ${max}`
      setValidationErrors({
        ...validationErrors,
        [cell.id]: errorMessage
      })
      toast.error(errorMessage)
      return false
    }

    /** 
     * Input is valid
     * Clear validation errors
     */
    const newValidationErrors = {...validationErrors}
    delete newValidationErrors[cell.id]
    setValidationErrors(newValidationErrors)
    return true
  }
  
  /**
   * Driving scenario model Inputs table columns
   */
  const columns: MRT_ColumnDef<VariablesTableRow>[] = useMemo(() => [
    {
      id: 'Variable',
      accessorFn: (row) => <div>{row?.display_name || (row?.variable_name ?? '')}</div>,
      header: 'Variable',
      enableEditing: false,
    },
    {
      id: 'Source Name',
      accessorKey: 'variable_name',
      header: 'Source Name',
      enableEditing: false,
    },
    {
      id: 'I/O',
      accessorKey: 'i_o',
      header: 'I/O',
      size: 110,
      enableEditing: false,
    },
    {
      id: 'Data Type',
      Cell: ({cell}) => {
        return <div>{cell.row.original.enum_data?.length ? "enumerated" : cell.row.original.data_type ?? ''}</div>
      },
      header: 'Data Type',
      size: 150,
      enableEditing: false,
    },
    {
      id: 'Description',
      accessorKey: 'description',
      Cell: ({renderedCellValue}) => {
        return <div>{renderedCellValue || ''}</div>
      },
      header: 'Description',
      size: 430,
      enableEditing: false,
    },
    {
      id: 'Minimum',
      accessorKey: 'min',
      Cell: ({cell, renderedCellValue}) => {
          return <div>{cell.row.original.enum_data?.length ? "" : renderedCellValue ?? ''}</div>
      },
      header: 'Min',
      size: 110,
      enableEditing: false,
    },
    {
      id: 'Maximum',
      accessorKey: 'max',
      Cell: ({cell, renderedCellValue}) => (
        <div>{cell.row.original.enum_data?.length ? "" : renderedCellValue ?? ""}</div>
      ),
      header: 'Max',
      size: 110,
      enableEditing: false,
    },
    {
      id: 'Value',
      header: 'Value',
      accessorKey: 'value',
      size: 150,
      enableEditing: true,
      muiEditTextFieldProps: ({cell}) => {
        return {
          ...getCommonEditTextFieldProps(cell),
          disabled: cell.row.original.enum_data?.length != 0 /** Toggled disableHostAccelerationProfile to allow edit on overide acceleration profile */
            || (cell.row.original.variable_name === "host.a_rate_mps2" && !disableHostAccelerationProfile)
        }
      },
      Cell: ({cell}) => {
        if (cell.row.original.enum_data?.length != 0) {
          const isHostAcclFlag = cell.row.original.variable_name === "host.accel_profile_flag"
          const _paramValue = cell.row.original.value

          return <ArgosSelect
            disabled={!hasWriteAccess}
            key={cell.row.index}
            multiple={false}
            style={{width: '100%'}}
            value={_paramValue}
            onChange={(e) => {
              if (isHostAcclFlag) {
                setDisableHostAccelerationProfile(!checkIfSelected(e))
              }
              handleSaveCell(e.target.value, cell.row.index)
            }}
          >
            {cell.row.original.enum_data?.map((element: EnumeratedVariableValues) =>
              <ArgosMenuItem key={element.value} value={element.value}>
                {element.alias}
              </ArgosMenuItem>)}
          </ArgosSelect>
        } 
        else {
          let val: any = cell.row.original.value ?? ''
          return <div>{val}</div>
        }
      },
    },
    {
      id: 'Default',
      Cell: ({cell}) => {
        const row = cell.row.original
        if (row.enum_data?.length) {
          return <div>{row.enum_data.filter((obj: EnumeratedVariableValues) => obj.value === row.default)[0].alias}</div>
        } else {
          return <div>{row.default}</div>
        }
      },
      header: 'Default',
      size: 150,
      enableEditing: false,
    },
    {
      id: 'Units',
      accessorKey: 'units',
      header: 'Units',
      size: 120,
      enableEditing: false,
    },
  ], [inputData])

  /**
   * Driving scenario model Outputs table columns
   */
  const outputColumns: MRT_ColumnDef<VariablesTableRow>[] = useMemo(() => {
    const _cols = columns.map(value => {
      if (value.id === 'Value') {
        const val: MRT_ColumnDef<VariablesTableRow> = {
          ...value,
          Cell: ({cell}) => {
            const display = outputsNotUpdated && typeof cell.row.original.value === 'number' ? 'initial' : 'none'
            let num: number | string = Number(Number.parseFloat(String(cell.row.original.value)).toFixed(5))
            if (Number.isNaN(num)) num = ""     //remove "NaN" from cell if there is no data to show

            if (display === "initial") {
              return (
                <div>
                  <b><span style={{color: ARGOS_ERROR}}>{num}</span></b>
                </div>
              )
            } 
            else {
              return (
                <div>
                  {num}
                </div>
              )
            }
          }
        }
        return (
          val
        )
      } 
      else {
        return value
      }
    })
    return _cols
  }, [columns, outputsNotUpdated])

  /**
   * Fetch model execution results
   */
  const modelExecutionUrl = SCENARIO_ANALYSIS_CONSTANTS.viewModel.endpoints.modelExecution
  const modelExecutionHandle = usePostRequest(modelExecutionUrl)
  const fetchModelExecution = async () => {
    setIsModelExecutionLoading(true)
    const body = { model, version, variation, "values": transformData(inputData)}
    modelExecutionHandle(body).then((data) => {
      const _outputData = tableData.filter((v: VariablesTableRow) =>
        v.i_o === "output"
      )?.map((row: VariablesTableRow, i: number) => {
        const value = data.run_results[row.variable_name]
        const rowCopy = {...row}
        rowCopy.value = value       
        return rowCopy
      })
      
      if (isShapeModel(model)) {
        setShapesData(data)
      } else {
        /* Set output data as transformed state for non-shape models */
        dispatch(setOutputData(_outputData))
      }
      dispatch(setOutputsNotUpdated(false))
    }).catch((e) => {
      console.log(e)
      toast.error("An error occurred while executing the model")
    }).then(() => {
      setIsModelExecutionLoading(false)
      setFirstExecute(true)
    })
  }
  
  return (
    <Container>
      <ArgosFlexbox style={{display: 'flex', gap: "10px", marginLeft: "auto", paddingBottom: "10px", alignItems: "center"}}>
        <Typography style={{color: ARGOS_ERROR, display: displayInvalidLabel, fontSize: 14}}>
          Invalid Outputs
        </Typography>
        <ArgosLoadingButton
          variant="text"
          size="large"
          fontSize={14}
          startIcon={<ArgosExecuteIcon/>}
          isLoading={isModelExecutionLoading}
          disabled={isModelExecutionLoading}
          onClick={() => fetchModelExecution()}
        >
          Execute
        </ArgosLoadingButton>
        <ArgosButton
          variant="text"
          size="large"
          fontSize={14}
          startIcon={<ArgosReloadIcon/>}
          disabled={isModelExecutionLoading}
          onClick={() => dispatch(setInputData(filterAndMap(tableData, "input")))}
        >
          Reset
        </ArgosButton>
      </ArgosFlexbox>
      <div style={{display: 'flex', flexGrow: 1, flexDirection: 'column', height: '90%'}}>
        <div style={{flexGrow: 1, height: '49%'}}>
          <ArgosTableSimple     //Input Table
            columns={columns}
            data={inputData}

            enablePagination={false}
            enableBottomToolbar={false}
            enableStickyHeader={true}
            muiTableBodyCellProps={({cell, column, table}) => ({
              onClick: () => {
                if (cell.row.original.enum_data.length == 0) {
                  table.setEditingCell(cell) //set editing cell
                  queueMicrotask(() => {
                    const textField = table.refs.editInputRefs.current[column.id]
                    if (textField) {
                      textField.focus()
                      textField.select?.()
                    }
                  })
                }

              }
            })}
            enableEditing={true}
            editDisplayMode={'cell'}
            enableTopToolbar={false}
          />
        </div>
        <div style={{background: 'gray', height: 2}}></div>
        <div style={{flexGrow: 1, height: '49%'}}>
          {!isShapeModel(model) ? (
            /***Output Table****/
            <ArgosTableSimple 
              columns={outputColumns}
              data={outputData}

              enableTableHead={false}
              enableTopToolbar={false}
              enablePagination={false}
              enableBottomToolbar={false}
            />
          ) : (
            <ShapePlot plotlyTraces={shapesData}/>
          )}
        </div>
      </div>
    </Container>
  )
}

export default VariablesTable
